import HelpPopup from '@wandb/weave/common/components/elements/HelpPopup';
import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import {
  DragDropProvider,
  DragHandle,
  DragRef,
  DragSource,
  DropTarget,
} from '@wandb/weave/common/containers/DragDropContainer';
import classNames from 'classnames';
import produce from 'immer';
import _ from 'lodash';
import React, {useCallback, useMemo, useRef, useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button, Checkbox, Icon, Input, Popup, Tab} from 'semantic-ui-react';

import {useProjectFieldsQuery} from '../../state/graphql/projectFieldsQuery';
import {
  keyStringToWBMenuOption,
  makeFetchProjectFieldOptions,
} from '../../util/dropdownQueries';
import {Query} from '../../util/queryTypes';
import * as Run from '../../util/runs';
import {EditableLabel} from '../elements/EditableLabel';
import LabeledOption from '../elements/LabeledOption';
import {DEFAULT_GRADIENT, GradientPicker} from '../GradientPicker';
import {PCColumn, PCConfig} from '../PanelParallelCoord/common';
import * as S from '../PanelParallelCoord/PanelParallelCoord.styles';
import ProjectFieldSelector from '../ProjectFieldSelector';

// This file defines the config/settings tab component for the Parallel Coordinates panel.
// Note: The main entry point for the Parallel Coordinates panel is in PanelParallelCoord.tsx

type PCSettingsProps = {
  config: PCConfig;
  updateConfig: (newConfig: PCConfig) => void;
  pageQuery: Query;
};

export const PCSettings: React.FC<PCSettingsProps> = React.memo(
  ({pageQuery, config, updateConfig}) => {
    const {entityName, projectName} = pageQuery;

    // tempColumns is used for drag+drop column reordering
    const [tempColumns, setTempColumns] = useState<PCColumn[] | undefined>(
      undefined
    );

    const {keys} = useProjectFieldsQuery({
      entityName,
      projectName,
      types: ['string', 'number', 'boolean'],
      columns: ['config', 'summary_metrics'],
      count: 50,
    });

    const renderColumns = useRef<PCColumn[]>(config.columns ?? []);
    const columnAccessors = useMemo(
      () => renderColumns.current.map(c => c.accessor ?? ''),
      // eslint-disable-next-line
      [renderColumns.current]
    );

    const defaultKey = useMemo(
      () => getDefaultKey(keys, columnAccessors),
      [keys, columnAccessors]
    );

    // Save the config and update the renderColumns ref
    const saveConfig = useCallback(
      (newConfig: Partial<PCConfig>) => {
        if (newConfig.columns != null) {
          renderColumns.current = newConfig.columns;
        }
        updateConfig(newConfig);
      },
      [updateConfig]
    );

    // Add a new column
    const addColumn = useCallback(() => {
      const newColumns = [...renderColumns.current, {accessor: defaultKey}];
      saveConfig({
        columns: newColumns,
      });
    }, [defaultKey, saveConfig]);

    // Remove a column
    const removeColumn = useCallback(
      (col: PCColumn) => {
        const newCols = produce(renderColumns.current, draft => {
          const colIndex = _.findIndex(draft, col);
          draft.splice(colIndex, 1);
        });
        saveConfig({columns: newCols});
      },
      [saveConfig]
    );

    // Set the column key
    const setColumnAccessor = useCallback(
      (col: PCColumn, newAccessor: string) => {
        const newCols = editColumn(
          renderColumns.current,
          col,
          c => (c.accessor = newAccessor)
        );
        saveConfig({columns: newCols});
      },
      [saveConfig]
    );

    // Set the display name for the column
    const renameColumn = useCallback(
      (col: PCColumn, newName: string) => {
        const newCols = editColumn(
          renderColumns.current,
          col,
          c => (c.displayName = newName)
        );
        saveConfig({columns: newCols});
      },
      [saveConfig]
    );

    // Toggle "inverted" or "log" attribute on the column
    const toggleColumnAttribute = useCallback(
      (col: PCColumn, attribute: 'inverted' | 'log') => {
        const newCols = editColumn(
          renderColumns.current,
          col,
          c => (c[attribute] = !c[attribute])
        );
        saveConfig({columns: newCols});
      },
      [saveConfig]
    );

    // Move column (drag+drop)
    const onDragEnter = useCallback(
      (col: PCColumn, dragRef: DragRef | null) => {
        if (dragRef == null || dragRef.id === col.accessor) {
          return;
        }
        const cols = tempColumns ?? renderColumns.current;
        const dragColIndex = _.findIndex(cols, {accessor: dragRef.id});
        const dropColIndex = _.findIndex(cols, col);
        if (dragColIndex === -1 || dropColIndex === -1) {
          return;
        }
        const dragCol = cols[dragColIndex];
        const newCols = produce(cols, draft => {
          draft.splice(dragColIndex, 1);
          draft.splice(dropColIndex, 0, dragCol);
        });
        setTempColumns(newCols);
      },
      [tempColumns]
    );

    const onDrop = useCallback(() => {
      if (tempColumns != null) {
        saveConfig({columns: tempColumns});
        setTempColumns(undefined);
      }
    }, [saveConfig, tempColumns]);

    const fetchProjectFieldOptions = useMemo(
      () =>
        makeFetchProjectFieldOptions(entityName, projectName, {
          types: ['string', 'number', 'boolean'],
          filterBy: key => {
            return !_.includes(columnAccessors, key.section + ':' + key.name);
          },
        }),
      [columnAccessors, entityName, projectName]
    );

    const axesTab = (
      <DragDropProvider>
        <Tab.Pane as="div" className="form-grid">
          {(tempColumns ?? renderColumns.current).map(col => {
            const isLog = !!col.log;
            const isInverted = !!col.inverted;
            const accessor = col.accessor ?? defaultKey;
            const accessorOption = keyStringToWBMenuOption(accessor);
            const dragDropID = col.accessor ?? '';

            return (
              <DropTarget
                key={accessor}
                partRef={{id: dragDropID}}
                onDrop={onDrop}
                onDragEnter={({dragRef}) => {
                  onDragEnter(col, dragRef);
                }}>
                <DragSource partRef={{id: dragDropID}}>
                  <div className="parallel-coordinates-settings__column">
                    <DragHandle partRef={{id: dragDropID}}>
                      <LegacyWBIcon title="" size="large" name="handle" />
                    </DragHandle>
                    <div className="parallel-coordinates-settings__column__dimension">
                      <Popup
                        content={Run.keyStringDisplayName(dragDropID)}
                        trigger={
                          <div>
                            <S.StyledSearchableSelect
                              infiniteScroll
                              disableClearable
                              options={fetchProjectFieldOptions}
                              value={accessor}
                              initialOption={accessorOption ?? undefined}
                              onSelect={v => {
                                setColumnAccessor(col, v as string);
                              }}
                            />
                          </div>
                        }
                      />
                    </div>
                    <div className="parallel-coordinates-settings__column__display-name">
                      <EditableLabel
                        key={col.accessor}
                        placeholder="Axis title"
                        onSave={(newName: string) => {
                          renameColumn(col, newName);
                        }}
                        // onBlur={() => this.setState({editingIndex: i})}
                        // editing={editingIndex === i}
                        serverText={col.displayName}
                        // onClick={() => false} // we use the menu option to trigger editing mode, rather than direct click
                      />
                    </div>
                    <Popup
                      content="Invert column"
                      inverted
                      position="top center"
                      size="tiny"
                      trigger={
                        <span>
                          <LegacyWBIcon
                            size="large"
                            title=""
                            name={isInverted ? 'sort-down' : 'sort-up'}
                            className={classNames(
                              `parallel-coordinates-settings__action`,
                              {
                                'parallel-coordinates-settings__action-active':
                                  isInverted,
                              }
                            )}
                            onClick={() => {
                              toggleColumnAttribute(col, 'inverted');
                            }}
                          />
                        </span>
                      }
                    />
                    <Popup
                      content="Log scale"
                      inverted
                      position="top center"
                      size="tiny"
                      trigger={
                        <span>
                          <LegacyWBIcon
                            name="log"
                            size="large"
                            title=""
                            className={classNames(
                              `parallel-coordinates-settings__action`,
                              {
                                'parallel-coordinates-settings__action-active':
                                  isLog,
                              }
                            )}
                            onClick={() => {
                              toggleColumnAttribute(col, 'log');
                            }}
                          />
                        </span>
                      }
                    />
                    <LegacyWBIcon
                      title=""
                      className="parallel-coordinates-settings__action"
                      size="large"
                      name="close"
                      onClick={() => {
                        removeColumn(col);
                      }}
                    />
                  </div>
                </DragSource>
              </DropTarget>
            );
          })}
          <Button
            className="parallel-coordinates-settings__add-column"
            data-test="parallel-coordinates-add-column"
            onClick={addColumn}>
            <Icon title="" name="plus" />
            Add a column
          </Button>
        </Tab.Pane>
      </DragDropProvider>
    );

    const legendTab = (
      <Tab.Pane as="div" className="form-grid">
        <LabeledOption
          label="Title"
          option={
            <Input
              placeholder={''}
              value={config.chartTitle ?? ''}
              onChange={(e, {value}) => {
                saveConfig({
                  chartTitle: value,
                });
              }}
            />
          }
        />
        <p className="chart-label">
          Legend Fields
          <HelpPopup helpText="Set extra variables to show when hovering over chart" />
        </p>
        <ProjectFieldSelector
          className="legend"
          disabled={false}
          selection
          query={pageQuery}
          types={['string', 'number', 'boolean']}
          defaultKeys={[
            'run:displayName',
            'run:name',
            'run:createdAt',
            'run:userName',
          ]}
          fluid
          multi
          value={config.legendFields ?? ['run:displayName']}
          setValue={value => saveConfig({legendFields: value})}
          searchByKeyAndText
        />
      </Tab.Pane>
    );

    const gradientTab = (
      <Tab.Pane as="div" className="form-grid">
        <div className="chart-label">
          <Checkbox
            label="Color lines based on last column value"
            checked={config.gradientColor ?? true}
            onChange={(e, data) => {
              saveConfig({
                gradientColor: data.checked,
              });
            }}
          />
        </div>
        {(config.gradientColor ?? true) && (
          <GradientPicker
            defaultGradient={
              config.customGradient
                ? {
                    type: 'customGradient',
                    gradient: config.customGradient,
                  }
                : DEFAULT_GRADIENT
            }
            setGradient={newGradient => {
              saveConfig({customGradient: newGradient});
            }}
          />
        )}
      </Tab.Pane>
    );

    const settingsPanes = [
      {
        menuItem: 'Axes',
        render: () => axesTab,
      },
      {
        menuItem: 'Legend',
        render: () => legendTab,
      },
      {
        menuItem: 'Gradient',
        render: () => gradientTab,
      },
    ];

    return (
      <div className="chart-settings parallel-coordinates-settings">
        <Tab
          panes={settingsPanes}
          menu={{
            secondary: true,
            pointing: true,
            className: 'chart-settings-tab-menu',
          }}
        />
      </div>
    );
  }
);

function editColumn(
  allCols: PCColumn[],
  col: PCColumn,
  mutateCol: (c: PCColumn) => void
): PCColumn[] {
  return produce(allCols, draft => {
    const colIndex = _.findIndex(draft, col);
    mutateCol(draft[colIndex]);
  });
}

function getDefaultKey(keys: string[], accessors: string[]): string {
  if (keys.length === 0) {
    return '';
  }

  // Suggest user-defined summary values before wandb summary values, which start with `_`
  const sortedKeys = _.sortBy(keys, key =>
    key.startsWith('summary:_') ? 1 : 0
  );

  const accessorSet: Set<string> = new Set(accessors);

  for (const key of sortedKeys) {
    if (!accessorSet.has(key)) {
      return key;
    }
  }
  return '';
}
